/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  addSupportTicketCommerceMessage,
  getSupportTicketById,
  hasUnreadSupportReply,
  listSupportTicketThreadMessages,
  markSupportTicketRead,
  submitSupportTicketUserResolution,
  type SupportThreadMessageRow,
  type SupportTicketRow,
  type UserTicketResolution,
} from '../../../shell/support/services/supportTicketsRepo';
import { listSupportTicketAttachments, uploadSupportTicketAttachments } from '../services/supportAttachmentsRepo';
import type { FeedbackAttachment, FeedbackAttachmentDraft } from '@shared/feedbackAttachments';
import { SupportAttachmentPicker } from './SupportAttachmentPicker';
import { SupportAttachmentList } from './SupportAttachmentList';
import { buildSupportTimeline, type SupportThreadMessage, type TimelineEntry } from '@shared/supportTicketTimeline';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { useModalShellScroll } from '../../../shared/modals/ModalShell';
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { showError } from '../../../shared/ui/appToast';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { useSupportUnreadCount } from '../../providers/SupportUnreadProvider';
import {
  supportStatusLabel,
  supportStatusTone,
} from '../screens/supportPresentation';
import { supportDetailStyles as styles } from '../screens/supportDetail.styles';

function mapRowsForTimeline(rows: SupportThreadMessageRow[], attachments: FeedbackAttachment[]): SupportThreadMessage[] {
  return rows.map((r) => ({
    id: r.id,
    author: r.author,
    body: r.body,
    staff_label: r.staff_label,
    created_at: r.created_at,
    attachments: attachments.filter((file) => file.thread_message_id === r.id),
  }));
}

function renderTimelineEntry(e: TimelineEntry, t: SupportTicketRow, key: string): React.ReactElement {
  if (e.kind === 'commerce_initial') {
    return (
      <View key={key} style={styles.threadBlock}>
        <View style={styles.threadHeader}>
          <Text style={styles.threadLabel}>Comercio</Text>
          <Text style={styles.threadTime}>{formatLocalDateTime(e.created_at)}</Text>
        </View>
        <Text style={styles.threadBody}>{e.body}</Text>
        <SupportAttachmentList attachments={e.attachments} />
      </View>
    );
  }
  if (e.kind === 'thread' && e.message.author === 'commerce') {
    return (
      <View key={key} style={styles.threadBlock}>
        <View style={styles.threadHeader}>
          <Text style={styles.threadLabel}>Comercio</Text>
          <Text style={styles.threadTime}>{formatLocalDateTime(e.message.created_at)}</Text>
        </View>
        <Text style={styles.threadBody}>{e.message.body}</Text>
        <SupportAttachmentList attachments={e.message.attachments} />
      </View>
    );
  }
  if (e.kind === 'thread' && e.message.author === 'staff') {
    return (
      <View key={key} style={[styles.threadBlock, styles.threadBlockStaff]}>
        <Text style={[styles.threadLabel, styles.threadLabelStaff]}>Equipo Vuelto</Text>
        <Text style={styles.threadBody}>{e.message.body}</Text>
        <SupportAttachmentList attachments={e.message.attachments} />
        <Text style={styles.threadMeta}>
          {formatLocalDateTime(e.message.created_at)}
          {e.message.staff_label ? ` · ${e.message.staff_label}` : ''}
          {e.showReadMeta
            ? t.read_by_user_at
              ? ` · Leído ${formatLocalDateTime(t.read_by_user_at)}`
              : ' · Aún no leído'
            : ''}
        </Text>
      </View>
    );
  }
  if (e.kind === 'survey') {
    return (
      <View key={key} style={styles.resolutionPanel}>
        <View style={styles.threadHeader}>
          <Text style={styles.threadLabel}>Encuesta</Text>
          <Text style={styles.threadTime}>{formatLocalDateTime(e.at)}</Text>
        </View>
        <Text style={styles.resolutionTitle}>
          {e.user_resolution === 'ok' ? 'Indicó que le resolvió' : 'Indicó que necesitaba seguimiento'}
        </Text>
        {e.user_resolution_note ? (
          <>
            <Text style={styles.resolutionNoteLabel}>Mensaje del comercio</Text>
            <Text style={styles.resolutionNoteText}>{e.user_resolution_note}</Text>
          </>
        ) : null}
      </View>
    );
  }
  return <View key={key} />;
}

type Props = {
  ticketId: string;
  /** Fila ya cargada en lista (workspace web); evita GET duplicado al abrir detalle. */
  initialTicket?: SupportTicketRow | null;
  /** When true, loads on mount/focus via ticketId changes (workspace). */
  active?: boolean;
  /** Own ScrollView inside panel (workspace). False when parent is ModalScrollScreen. */
  scrollable?: boolean;
  /** Panel derecho del workspace web. */
  workspaceMode?: boolean;
  /** Sincroniza fila en listado (badge «Nuevo», encuesta, etc.). */
  onTicketUpdated?: (ticket: SupportTicketRow) => void;
  onRegisterReload?: (reload: () => Promise<void>) => void;
};

export function SupportTicketDetailPanel({
  ticketId,
  initialTicket,
  active = true,
  scrollable = true,
  workspaceMode = false,
  onTicketUpdated,
  onRegisterReload,
}: Props) {
  const scrollInputIntoView = useModalShellScroll();
  const surveyInputRef = useRef<TextInput>(null);
  const followInputRef = useRef<TextInput>(null);
  const { refresh: refreshSupportUnread } = useSupportUnreadCount();
  const [ticket, setTicket] = useState<SupportTicketRow | null>(null);
  const [thread, setThread] = useState<SupportThreadMessageRow[]>([]);
  const [attachments, setAttachments] = useState<FeedbackAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [resolutionBusy, setResolutionBusy] = useState(false);
  const [notResolvedMode, setNotResolvedMode] = useState(false);
  const [notResolvedNote, setNotResolvedNote] = useState('');
  const [commerceNote, setCommerceNote] = useState('');
  const [commerceAttachments, setCommerceAttachments] = useState<FeedbackAttachmentDraft[]>([]);
  const [commerceBusy, setCommerceBusy] = useState(false);

  useEffect(() => {
    setTicket(null);
    setThread([]);
    setAttachments([]);
    setError(null);
    setThreadError(null);
    setLoading(true);
    setNotResolvedMode(false);
    setNotResolvedNote('');
    setCommerceNote('');
    setCommerceAttachments([]);
  }, [ticketId]);

  const notifyTicketUpdated = useCallback(
    (row: SupportTicketRow) => {
      onTicketUpdated?.(row);
    },
    [onTicketUpdated]
  );

  const load = useCallback(async (): Promise<SupportTicketRow | null> => {
    if (!ticketId) {
      setError('Falta el identificador del reporte.');
      setTicket(null);
      setThread([]);
      return null;
    }
    setError(null);
    const row =
      initialTicket?.id === ticketId ? initialTicket : await getSupportTicketById(ticketId);
    if (!row) {
      setError('No encontramos este reporte o no tenés permiso para verlo.');
      setTicket(null);
      setThread([]);
      return null;
    }
    let msgs: SupportThreadMessageRow[] = [];
    let files: FeedbackAttachment[] = [];
    try {
      [msgs, files] = await Promise.all([
        listSupportTicketThreadMessages(ticketId),
        listSupportTicketAttachments(ticketId),
      ]);
    } catch {
      /* compatibilidad durante el despliegue aditivo */
      try {
        msgs = await listSupportTicketThreadMessages(ticketId);
      } catch {
        setThreadError('No pudimos cargar el hilo. Probá actualizar el reporte.');
      }
    }
    setThread(msgs);
    setAttachments(files);
    setTicket(row);
    return row;
  }, [initialTicket, ticketId]);

  const runLoad = useCallback(async (): Promise<void> => {
    if (!active || !ticketId) return;
    setLoading(true);
    try {
      await load();
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message?: string }).message) : 'Error al cargar.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [active, load, ticketId]);

  const applyReadAck = useCallback(
    async (row: SupportTicketRow): Promise<SupportTicketRow> => {
      if (!hasUnreadSupportReply(row)) return row;
      await markSupportTicketRead(row.id);
      const readAt = new Date().toISOString();
      const updated = { ...row, read_by_user_at: readAt };
      setTicket(updated);
      notifyTicketUpdated(updated);
      void refreshSupportUnread();
      return updated;
    },
    [notifyTicketUpdated, refreshSupportUnread],
  );

  /** Abrir el hilo marca leído (sin paso manual «Entendido»). */
  useEffect(() => {
    if (!ticket || ticket.id !== ticketId) return;
    if (!hasUnreadSupportReply(ticket)) return;
    void applyReadAck(ticket).catch(captureCatch('shell', 'support-read-ack-open', undefined));
  }, [applyReadAck, ticket, ticketId]);

  useEffect(() => {
    if (!scrollable) return undefined;
    void runLoad();
  }, [runLoad, scrollable]);

  useFocusEffect(
    useCallback(() => {
      if (scrollable) return undefined;
      void runLoad();
      return undefined;
    }, [runLoad, scrollable])
  );

  useEffect(() => {
    if (!onRegisterReload) return;
    onRegisterReload(() => runLoad());
    return () => onRegisterReload(async () => {});
  }, [onRegisterReload, runLoad]);

  const ticketStale = ticket != null && ticket.id !== ticketId;

  if (loading && (!ticket || ticketStale)) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={ticketColors.gold} size="large" />
        <Text style={styles.loadingCaption}>Cargando hilo…</Text>
      </View>
    );
  }

  if (error || !ticket || ticketStale) {
    return (
      <View style={styles.padded}>
        <Text style={styles.errorText}>{error ?? 'No disponible.'}</Text>
      </View>
    );
  }

  const raw = mapRowsForTimeline(thread, attachments);
  const timeline = buildSupportTimeline({ ...ticket, initial_attachments: attachments.filter((file) => file.thread_message_id == null) }, raw);
  const hasStaff = thread.some((m) => m.author === 'staff') || Boolean(ticket.public_reply && ticket.replied_at);
  const canAnswerResolution = hasStaff && !ticket.user_resolution;

  const applyResolution = async (resolution: UserTicketResolution, note?: string) => {
    setResolutionBusy(true);
    try {
      if (hasUnreadSupportReply(ticket)) {
        await applyReadAck(ticket).catch(captureCatch('shell', 'support-read-ack-resolution', undefined));
      }
      await submitSupportTicketUserResolution(ticket.id, resolution, note);
      const next = await getSupportTicketById(ticket.id);
      const nextThread = await listSupportTicketThreadMessages(ticket.id).catch(() => thread);
      if (next) {
        setTicket(next);
        notifyTicketUpdated(next);
      }
      setThread(nextThread);
      setNotResolvedMode(false);
      setNotResolvedNote('');
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object'
          ? String((e as { message?: string; details?: string }).message ?? (e as { details?: string }).details ?? 'No se pudo guardar. Probá de nuevo.')
          : 'No se pudo guardar. Probá de nuevo.';
      showError('No se pudo enviar', msg);
    } finally {
      setResolutionBusy(false);
    }
  };

  const sendCommerceFollowUp = async () => {
    const t = commerceNote.trim();
    if (t.length < 1) return;
    setCommerceBusy(true);
    try {
      const messageId = await addSupportTicketCommerceMessage(ticket.id, t);
      if (commerceAttachments.length > 0) {
        await uploadSupportTicketAttachments({
          ticketId: ticket.id,
          threadMessageId: messageId,
          author: 'commerce',
          drafts: commerceAttachments,
        });
      }
      setCommerceNote('');
      setCommerceAttachments([]);
      if (hasUnreadSupportReply(ticket)) {
        await applyReadAck(ticket).catch(captureCatch('shell', 'support-read-ack-commerce', undefined));
      }
      const [next, th, files] = await Promise.all([
        getSupportTicketById(ticket.id),
        listSupportTicketThreadMessages(ticket.id),
        listSupportTicketAttachments(ticket.id),
      ]);
      if (next) {
        setTicket(next);
        notifyTicketUpdated(next);
      }
      setThread(th);
      setAttachments(files);
      void refreshSupportUnread();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object'
          ? String((e as { message?: string; details?: string }).message ?? (e as { details?: string }).details ?? 'Probá de nuevo.')
          : 'Probá de nuevo.';
      showError('No se pudo enviar', msg);
    } finally {
      setCommerceBusy(false);
    }
  };

  const contentStyle = workspaceMode ? styles.contentWorkspace : styles.content;

  const body = (
    <>
      <View style={styles.heroMetaRow}>
        <StatusPill label={supportStatusLabel(ticket.status)} tone={supportStatusTone(ticket.status)} />
        <Text style={styles.heroDate}>{formatLocalDateTime(ticket.created_at)}</Text>
      </View>

      {threadError ? <Text style={styles.errorText}>{threadError}</Text> : null}
      {timeline.map((e, i) => renderTimelineEntry(e, ticket, `tl-${i}-${e.kind}`))}

      {!hasStaff ? (
        <Text style={styles.pendingText}>
          Cuando el equipo responda, vas a ver el mensaje acá.
        </Text>
      ) : null}

      {canAnswerResolution ? (
        <View style={styles.surveyCard} testID="support.detail.survey">
          <Text style={styles.surveyCardTitle}>¿Te sirvió la respuesta?</Text>
          {resolutionBusy ? <ActivityIndicator color={ticketColors.gold} style={{ marginVertical: 4 }} /> : null}
          {!notResolvedMode ? (
            <View style={styles.surveyActions}>
              <TouchableOpacity
                style={[styles.surveyBtn, styles.surveyBtnPrimary]}
                onPress={() => void applyResolution('ok')}
                disabled={resolutionBusy}
                accessibilityRole="button"
                accessibilityLabel="Sí, me resolvió"
              >
                <Text style={styles.surveyBtnPrimaryText}>Sí, me resolvió</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.surveyBtn}
                onPress={() => setNotResolvedMode(true)}
                disabled={resolutionBusy}
                accessibilityRole="button"
                accessibilityLabel="Aún no, necesito seguimiento"
              >
                <Text style={styles.surveyBtnText}>Aún no</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.surveyFollowUp}>
              <TouchableOpacity
                onPress={() => {
                  setNotResolvedMode(false);
                  setNotResolvedNote('');
                }}
                style={styles.surveyCancel}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Text style={styles.surveyCancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TextInput
                ref={surveyInputRef}
                style={styles.textInput}
                multiline
                value={notResolvedNote}
                onChangeText={setNotResolvedNote}
                placeholder="Contanos qué falta (opcional)…"
                placeholderTextColor={ticketColors.ink3}
                maxLength={1000}
                editable={!resolutionBusy}
                textAlignVertical="top"
                accessibilityLabel="Comentario sobre el seguimiento"
                onFocus={() => scrollInputIntoView(surveyInputRef)}
              />
              <TouchableOpacity
                style={[styles.sendBtn, resolutionBusy && styles.sendBtnDisabled]}
                onPress={() => void applyResolution('not_resolved', notResolvedNote)}
                disabled={resolutionBusy}
                accessibilityRole="button"
                accessibilityLabel="Enviar y pedir seguimiento"
              >
                {resolutionBusy ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.sendBtnText}>Enviar</Text>
                )}
              </TouchableOpacity>
            </View>
          )}
        </View>
      ) : null}

      {ticket.status === 'closed' ? (
        <Text style={styles.pendingText}>El caso está cerrado. Si escribís, se reabre.</Text>
      ) : null}

      <View style={styles.followSection}>
        <Text style={styles.sectionLabel}>Escribir al equipo</Text>
        <TextInput
          ref={followInputRef}
          style={styles.textInput}
          multiline
          value={commerceNote}
          onChangeText={setCommerceNote}
          placeholder="Escribí un seguimiento…"
          placeholderTextColor={ticketColors.ink3}
          maxLength={20000}
          editable={!commerceBusy}
          textAlignVertical="top"
          accessibilityLabel="Mensaje de seguimiento al equipo Vuelto"
          onFocus={() => scrollInputIntoView(followInputRef)}
        />
        <SupportAttachmentPicker value={commerceAttachments} onChange={setCommerceAttachments} disabled={commerceBusy} />
        <TouchableOpacity
          style={[styles.sendBtn, (commerceBusy || !commerceNote.trim()) && styles.sendBtnDisabled]}
          onPress={() => void sendCommerceFollowUp()}
          disabled={commerceBusy || !commerceNote.trim()}
          accessibilityRole="button"
          accessibilityLabel="Enviar seguimiento"
        >
          {commerceBusy ? <ActivityIndicator color={ticketColors.goldInk} /> : <Text style={styles.sendBtnText}>Enviar</Text>}
        </TouchableOpacity>
      </View>
    </>
  );

  if (scrollable) {
    return (
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={contentStyle}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          <RefreshControl
            refreshing={loading}
            onRefresh={() => void runLoad()}
            colors={[ticketColors.gold]}
            tintColor={ticketColors.gold}
          />
        }
      >
        {body}
      </ScrollView>
    );
  }

  return <View style={contentStyle}>{body}</View>;
}
